import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, map, catchError, of, withLatestFrom, tap } from 'rxjs';
import { Store } from '@ngrx/store';

import { IDjangoErrors } from '../../../models/DjangoErrors';
import { OrganizationService } from '../../../services/organization.service';
import { OrganizationUiActions, OrganizationApiActions } from '../actions/index';
import { IOrganization } from '../../../models/organization';
import { OrganizationState } from '../reducers/organization.reducer';
import * as organizationSelector from '../selectors/organization.selectors';
import { ToastService } from '../../../services/toast.service';

@Injectable()
export class OrganizationEffects {
  private actions$ = inject(Actions);
  private organizationService = inject(OrganizationService);
  private store = inject(Store<OrganizationState>);
  private toastService = inject(ToastService);

  organizationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationUiActions.GetOrganizations),
      concatMap(({ active }) =>
        this.organizationService.getOrganizations(active).pipe(
          map((results: IOrganization[]) => OrganizationApiActions.GetOrganizationListSuccess({ payload: results })),
          catchError((error) =>
            of(OrganizationApiActions.GetOrganizationListFailure({ errors: error as IDjangoErrors }))
          )
        )
      )
    )
  );

  createOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationUiActions.CreateOrganization),
      concatMap(({ organization }) =>
        this.organizationService.createOrganization(organization).pipe(
          map((result: IOrganization) => OrganizationApiActions.CreateOrganizationSuccess({ payload: result })),
          catchError((error) =>
            of(OrganizationApiActions.CreateOrganizationFailure({ errors: error as IDjangoErrors }))
          )
        )
      )
    )
  );

  updateOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationUiActions.UpdateOrganization),
      concatMap(({ organization }) =>
        this.organizationService.updateOrganization(organization).pipe(
          map((result: IOrganization) => OrganizationApiActions.UpdateOrganizationSuccess({ payload: result })),
          catchError((error) =>
            of(OrganizationApiActions.UpdateOrganizationFailure({ errors: error as IDjangoErrors }))
          )
        )
      )
    )
  );

  patchOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationUiActions.PatchOrganization),
      concatMap(({ id, patch }) =>
        this.organizationService.patchOrganization(id, patch).pipe(
          map((result: IOrganization) => OrganizationApiActions.PatchOrganizationSuccess({ payload: result, id })),
          catchError((error) => {
            this.toastService.show('Failed to update organization default access setting', 'error');
            return of(OrganizationApiActions.PatchOrganizationFailure({ errors: error as IDjangoErrors }));
          })
        )
      )
    )
  );

  patchOrganizationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrganizationApiActions.PatchOrganizationSuccess),
        withLatestFrom(this.store.select(organizationSelector.getOrgList)),
        tap(([{ payload, id }, organizationList]) => {
          const organization = organizationList.find((org) => org.id === id);
          const orgName = organization?.display_name || 'organization';
          if (payload.default_approve_analytical_dataset_requests !== undefined) {
            const setting = payload.default_approve_analytical_dataset_requests ? 'enabled' : 'disabled';
            this.toastService.show(
              `Reportable-pathogen auto-approval ${setting} for ${orgName}`,
              'success',
            );
          } else {
            this.toastService.show(`Updated ${orgName}`, 'success');
          }
        })
      ),
    { dispatch: false }
  );

  deleteOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationUiActions.DeleteOrganization),
      withLatestFrom(this.store.select(organizationSelector.getOrgList)),
      concatMap(([{ id }, organizationList]) => {
        // Get organization name before deletion
        const organization = organizationList.find((org) => org.id === id);
        const orgName = organization?.display_name || 'organization';

        return this.organizationService.deleteOrganization(id).pipe(
          map(() => OrganizationApiActions.DeleteOrganizationSuccess({ id, orgName })),
          catchError((error) => {
            this.toastService.show(`Failed to delete ${orgName}`, 'error');
            return of(OrganizationApiActions.DeleteOrganizationFailure({ errors: error as IDjangoErrors }));
          })
        );
      })
    )
  );

  deleteOrganizationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrganizationApiActions.DeleteOrganizationSuccess),
        tap(({ orgName }) => {
          this.toastService.show(`${orgName} has been deleted`, 'success');
        })
      ),
    { dispatch: false }
  );

  // Refresh organization list after create, update, patch, or delete
  refreshOrganizationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        OrganizationApiActions.CreateOrganizationSuccess,
        OrganizationApiActions.UpdateOrganizationSuccess,
        OrganizationApiActions.PatchOrganizationSuccess,
        OrganizationApiActions.DeleteOrganizationSuccess
      ),
      withLatestFrom(this.store.select(organizationSelector.getCurrentActiveFilter)),
      map(([, currentActiveFilter]) =>
        OrganizationUiActions.GetOrganizations({
          active: currentActiveFilter !== undefined ? currentActiveFilter : true,
        })
      )
    )
  );
}
